import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from './firebase';
import { useAdmin } from './hooks/useAdmin';
import AdminAppEngagement from './AdminAppEngagement';
import VisitorUsersTab from './VisitorUsersTab';
import VisitorComparisonsTab from './VisitorComparisonsTab';
import VisitorShareTab from './VisitorShareTab';
import './VisitorStatistics.css';

export default function VisitorStatistics() {
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    totalVisits: 0,
    uniqueVisitors: 0,
    pageViews: {},
    dailyStats: [],
    topPages: [],
    deviceStats: {
      mobile: 0,
      desktop: 0,
      tablet: 0
    },
    browserStats: {},
    recentVisits: [],
    interactions: [],
    interactionStats: {}
  });
  const [timeRange, setTimeRange] = useState('7days'); // 7days, 30days, all
  const [filterPage, setFilterPage] = useState('all');
  const [activeStatsTab, setActiveStatsTab] = useState('overview'); // overview, interactions, users, share, comparisons, app
  const [shareEvents, setShareEvents] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [comparisonEvents, setComparisonEvents] = useState([]);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [comparisonError, setComparisonError] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      loadStatistics();
    }
  }, [isAdmin, timeRange]);

  useEffect(() => {
    if (isAdmin && activeStatsTab === 'share') {
      loadShareEvents();
    }
    if (isAdmin && activeStatsTab === 'comparisons') {
      loadComparisonEvents();
    }
  }, [isAdmin, activeStatsTab, timeRange]);

  const loadShareEvents = async () => {
    setShareLoading(true);
    setShareError(null);
    try {
      const now = new Date();
      let startDate = new Date(0);
      if (timeRange === '7days') startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      else if (timeRange === '30days') startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const ref = collection(db, 'shareCardEvents');
      const q = timeRange === 'all'
        ? query(ref, orderBy('timestamp', 'desc'), limit(1000))
        : query(ref, where('timestamp', '>=', startDate), orderBy('timestamp', 'desc'), limit(1000));
      const snap = await getDocs(q);
      setShareEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error loading share events:', e);
      setShareError(e);
    } finally {
      setShareLoading(false);
    }
  };

  const loadComparisonEvents = async () => {
    setComparisonLoading(true);
    setComparisonError(null);
    try {
      const now = new Date();
      let startDate = new Date(0);
      if (timeRange === '7days') startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      else if (timeRange === '30days') startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const ref = collection(db, 'dancerComparisons');
      const q = timeRange === 'all'
        ? query(ref, orderBy('timestamp', 'desc'), limit(2000))
        : query(ref, where('timestamp', '>=', startDate), orderBy('timestamp', 'desc'), limit(2000));
      const snap = await getDocs(q);
      setComparisonEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error loading comparison events:', e);
      setComparisonError(e);
    } finally {
      setComparisonLoading(false);
    }
  };

  const loadStatistics = async () => {
    setLoading(true);
    try {
      // Calculate date range
      const now = new Date();
      let startDate = new Date(0); // Beginning of time
      if (timeRange === '7days') {
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (timeRange === '30days') {
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      }

      // Fetch visitor logs from Firestore
      const visitsRef = collection(db, 'visitorLogs');
      let q = query(visitsRef, orderBy('timestamp', 'desc'), limit(10000));
      
      if (timeRange !== 'all') {
        q = query(visitsRef, 
          where('timestamp', '>=', startDate),
          orderBy('timestamp', 'desc'),
          limit(10000)
        );
      }

      const snapshot = await getDocs(q);
      const visits = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Process statistics
      const stats = processStatistics(visits);
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading visitor statistics:', error);
    }
    setLoading(false);
  };

  const processStatistics = (visits) => {
    const uniqueVisitorsSet = new Set();
    const pageViews = {};
    const dailyStatsMap = {};
    const deviceStats = { mobile: 0, desktop: 0, tablet: 0 };
    const browserStats = {};
    const loggedInUsersMap = {};
    const sessionMap = {};
    const interactions = [];
    const interactionStats = {
      link_click: { total: 0, byButton: {} },
      popup_open: { total: 0, byPopup: {} },
      audio_play: { total: 0, byTrack: {} }
    };


    visits.forEach(visit => {
      // Skip admin visits (in case any got through)
      if (visit.userEmail === 'giannisaliev@gmail.com' || visit.isAdmin) {
        return;
      }

      // Handle interactions separately
      if (visit.type === 'interaction') {
        const date = visit.timestamp?.toDate?.() || new Date(visit.timestamp);
        interactions.push({
          ...visit,
          date: date
        });
        
        // Track interaction stats
        const iType = visit.interactionType;
        if (interactionStats[iType]) {
          interactionStats[iType].total++;
          
          if (iType === 'link_click' && visit.details?.button) {
            const buttonKey = `${visit.details.button} - ${visit.details.competition || 'Unknown'}`;
            interactionStats[iType].byButton[buttonKey] = (interactionStats[iType].byButton[buttonKey] || 0) + 1;
          }
          if (iType === 'popup_open' && visit.details?.popup) {
            const popupKey = `${visit.details.popup} - ${visit.details.competition || 'Unknown'}`;
            interactionStats[iType].byPopup[popupKey] = (interactionStats[iType].byPopup[popupKey] || 0) + 1;
          }
          if (iType === 'audio_play' && visit.details?.track) {
            const trackKey = `${visit.details.track} (${visit.details.competition || 'Unknown'})`;
            interactionStats[iType].byTrack[trackKey] = (interactionStats[iType].byTrack[trackKey] || 0) + 1;
          }
        }

        // Also track interactions for logged-in users
        if (visit.isAuthenticated && visit.userEmail) {
          const userKey = visit.userEmail;
          if (!loggedInUsersMap[userKey]) {
            loggedInUsersMap[userKey] = {
              email: visit.userEmail,
              name: visit.userName || 'No name',
              userId: visit.userId,
              sessions: [],
              totalPages: 0,
              interactions: [],
              pageVisits: [],
              firstSeen: date,
              lastSeen: date
            };
          }
          if (!loggedInUsersMap[userKey].interactions) {
            loggedInUsersMap[userKey].interactions = [];
          }
          loggedInUsersMap[userKey].interactions.push({
            type: visit.interactionType,
            details: visit.details,
            timestamp: date
          });
          loggedInUsersMap[userKey].lastSeen = date > loggedInUsersMap[userKey].lastSeen ? date : loggedInUsersMap[userKey].lastSeen;
        }
        
        return; // Don't count interactions as page views
      }

      // Unique visitors (by IP or sessionId)
      const visitorId = visit.sessionId || visit.ip || visit.userId;
      if (visitorId) uniqueVisitorsSet.add(visitorId);

      // Page views
      const page = visit.page || visit.path || 'Unknown';
      pageViews[page] = (pageViews[page] || 0) + 1;

      // Daily stats
      const date = visit.timestamp?.toDate?.() || new Date(visit.timestamp);
      const dateKey = date.toISOString().split('T')[0];
      if (!dailyStatsMap[dateKey]) {
        dailyStatsMap[dateKey] = { date: dateKey, visits: 0, uniqueVisitors: new Set() };
      }
      dailyStatsMap[dateKey].visits++;
      if (visitorId) dailyStatsMap[dateKey].uniqueVisitors.add(visitorId);

      // Device stats
      const device = visit.device || detectDevice(visit.userAgent);
      deviceStats[device] = (deviceStats[device] || 0) + 1;

      // Browser stats
      const browser = visit.browser || detectBrowser(visit.userAgent);
      browserStats[browser] = (browserStats[browser] || 0) + 1;

      // Track logged-in users and their sessions
      if (visit.isAuthenticated && visit.userEmail) {
        const userKey = visit.userEmail;
        
        if (!loggedInUsersMap[userKey]) {
          loggedInUsersMap[userKey] = {
            email: visit.userEmail,
            name: visit.userName || 'No name',
            userId: visit.userId,
            sessions: [],
            totalPages: 0,
            interactions: [],
            pageVisits: [],
            firstSeen: date,
            lastSeen: date
          };
        }

        loggedInUsersMap[userKey].totalPages++;
        loggedInUsersMap[userKey].pageVisits.push({ page, timestamp: date });
        loggedInUsersMap[userKey].lastSeen = date > loggedInUsersMap[userKey].lastSeen ? date : loggedInUsersMap[userKey].lastSeen;
        
        // Track sessions
        if (visit.sessionId) {
          if (!sessionMap[visit.sessionId]) {
            sessionMap[visit.sessionId] = {
              sessionId: visit.sessionId,
              userEmail: visit.userEmail,
              pages: [],
              startTime: visit.sessionStartTime?.toDate?.() || date,
              lastActivity: date,
              duration: 0
            };
          }
          
          sessionMap[visit.sessionId].pages.push({
            page: page,
            timestamp: date
          });
          sessionMap[visit.sessionId].lastActivity = date > sessionMap[visit.sessionId].lastActivity ? date : sessionMap[visit.sessionId].lastActivity;
          sessionMap[visit.sessionId].duration = visit.sessionDuration || 0;
        }
      }
    });

    // Process logged-in users - attach sessions
    Object.values(loggedInUsersMap).forEach(user => {
      user.sessions = Object.values(sessionMap)
        .filter(session => session.userEmail === user.email)
        .map(session => {
          const pages = [...session.pages].sort((a, b) => a.timestamp - b.timestamp);
          return { ...session, pages, startTime: pages.length ? pages[0].timestamp : session.startTime };
        })
        .sort((a, b) => b.lastActivity - a.lastActivity);
      user.pageVisits.sort((a, b) => b.timestamp - a.timestamp);
      user.interactions = [...user.interactions].sort((a, b) => b.timestamp - a.timestamp);
    });

    const loggedInUsers = Object.values(loggedInUsersMap)
      .sort((a, b) => b.lastSeen - a.lastSeen); // Sort by most recent activity

    // Convert daily stats to array and sort
    const dailyStats = Object.values(dailyStatsMap)
      .map(day => ({
        ...day,
        uniqueVisitors: day.uniqueVisitors.size
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Top pages
    const topPages = Object.entries(pageViews)
      .map(([page, views]) => ({ page, views }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 20);

    // Recent visits (last 50) - exclude admin and interactions
    const recentVisits = visits
      .filter(v => v.userEmail !== 'giannisaliev@gmail.com' && !v.isAdmin && v.type !== 'interaction')
      .slice(0, 50);

    // Recent interactions (last 100)
    const recentInteractions = interactions
      .sort((a, b) => b.date - a.date)
      .slice(0, 100);

    return {
      totalVisits: visits.filter(v => v.userEmail !== 'giannisaliev@gmail.com' && !v.isAdmin && v.type !== 'interaction').length,
      totalInteractions: interactions.length,
      uniqueVisitors: uniqueVisitorsSet.size,
      pageViews,
      dailyStats,
      topPages,
      deviceStats,
      browserStats,
      recentVisits,
      loggedInUsers,
      interactions: recentInteractions,
      interactionStats
    };
  };

  const detectDevice = (userAgent) => {
    if (!userAgent) return 'desktop';
    const ua = userAgent.toLowerCase();
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera mini|opera mobi|skyfire|maemo|windows phone|palm|iemobile|symbian|symbianos|fennec/i.test(ua)) return 'mobile';
    return 'desktop';
  };

  const detectBrowser = (userAgent) => {
    if (!userAgent) return 'Unknown';
    const ua = userAgent.toLowerCase();
    if (ua.includes('firefox')) return 'Firefox';
    if (ua.includes('edg')) return 'Edge';
    if (ua.includes('chrome')) return 'Chrome';
    if (ua.includes('safari')) return 'Safari';
    if (ua.includes('opera')) return 'Opera';
    return 'Other';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTimestamp = (timestamp) => {
    const date = timestamp?.toDate?.() || new Date(timestamp);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getInteractionIcon = (type) => {
    switch(type) {
      case 'link_click': return '🔗';
      case 'popup_open': return '📦';
      case 'audio_play': return '🎵';
      default: return '👆';
    }
  };

  const getInteractionLabel = (type) => {
    switch(type) {
      case 'link_click': return 'Link Click';
      case 'popup_open': return 'Popup Opened';
      case 'audio_play': return 'Audio Played';
      default: return 'Interaction';
    }
  };

  const formatInteractionDetails = (interaction) => {
    const details = interaction.details || {};
    if (interaction.interactionType === 'link_click') {
      return `${details.button || 'Link'} - ${details.competition || 'Unknown'}`;
    }
    if (interaction.interactionType === 'popup_open') {
      return `${details.popup || 'Popup'} - ${details.competition || 'Unknown'}`;
    }
    if (interaction.interactionType === 'audio_play') {
      return `${details.track || 'Track'} (${details.competition || 'Unknown'})`;
    }
    return JSON.stringify(details);
  };

  if (adminLoading) {
    return (
      <div className="visitor-stats-container">
        <div className="loading-message">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="visitor-stats-container">
        <div className="access-denied">
          <h2>🔒 Access Denied</h2>
          <p>You don't have permission to view visitor statistics.</p>
        </div>
      </div>
    );
  }

  const filteredPages = filterPage === 'all' 
    ? statistics.topPages 
    : statistics.topPages.filter(p => p.page.includes(filterPage));

  return (
    <div className="visitor-stats-container">
      <div className="visitor-stats-header">
        <h1>📊 Visitor Statistics</h1>
        <div className="stats-controls">
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
            className="time-range-selector"
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* Stats Navigation Tabs */}
      <div className="stats-nav-tabs">
        <button 
          className={`stats-nav-tab ${activeStatsTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveStatsTab('overview')}
        >
          📈 Overview
        </button>
        <button 
          className={`stats-nav-tab ${activeStatsTab === 'interactions' ? 'active' : ''}`}
          onClick={() => setActiveStatsTab('interactions')}
        >
          👆 Interactions ({statistics.totalInteractions || 0})
        </button>
        <button 
          className={`stats-nav-tab ${activeStatsTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveStatsTab('users')}
        >
          👤 Users ({statistics.loggedInUsers?.length || 0})
        </button>
        <button
          className={`stats-nav-tab ${activeStatsTab === 'share' ? 'active' : ''}`}
          onClick={() => setActiveStatsTab('share')}
        >
          🖼️ Share Cards
        </button>
        <button
          className={`stats-nav-tab ${activeStatsTab === 'comparisons' ? 'active' : ''}`}
          onClick={() => setActiveStatsTab('comparisons')}
        >
          ⚔️ Comparisons
        </button>
        <button
          className={`stats-nav-tab ${activeStatsTab === 'app' ? 'active' : ''}`}
          onClick={() => setActiveStatsTab('app')}
        >
          📲 App & Alerts
        </button>
      </div>

      {/* APP & ALERTS TAB: independent of the visitor-log data, so it renders outside the loading gate */}
      {activeStatsTab === 'app' && isAdmin && <AdminAppEngagement />}

      {activeStatsTab === 'app' ? null : loading ? (
        <div className="loading-message">Loading statistics...</div>
      ) : (
        <>
          {/* Summary Cards - Always visible */}
          <div className="stats-summary-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <div className="stat-label">Total Visits</div>
                <div className="stat-value">{statistics.totalVisits.toLocaleString()}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🎯</div>
              <div className="stat-content">
                <div className="stat-label">Unique Visitors</div>
                <div className="stat-value">{statistics.uniqueVisitors.toLocaleString()}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">👆</div>
              <div className="stat-content">
                <div className="stat-label">Interactions</div>
                <div className="stat-value">{(statistics.totalInteractions || 0).toLocaleString()}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📄</div>
              <div className="stat-content">
                <div className="stat-label">Pages Viewed</div>
                <div className="stat-value">{Object.keys(statistics.pageViews).length}</div>
              </div>
            </div>
          </div>

          {/* INTERACTIONS TAB */}
          {activeStatsTab === 'interactions' && (
            <>
              {/* Interaction Stats Summary */}
              <div className="stats-section">
                <h2>👆 Interaction Summary</h2>
                <div className="interaction-summary-grid">
                  <div className="interaction-stat-card">
                    <div className="interaction-stat-icon">🔗</div>
                    <div className="interaction-stat-content">
                      <div className="interaction-stat-label">Link Clicks</div>
                      <div className="interaction-stat-value">{statistics.interactionStats?.link_click?.total || 0}</div>
                    </div>
                  </div>
                  <div className="interaction-stat-card">
                    <div className="interaction-stat-icon">📦</div>
                    <div className="interaction-stat-content">
                      <div className="interaction-stat-label">Popups Opened</div>
                      <div className="interaction-stat-value">{statistics.interactionStats?.popup_open?.total || 0}</div>
                    </div>
                  </div>
                  <div className="interaction-stat-card">
                    <div className="interaction-stat-icon">🎵</div>
                    <div className="interaction-stat-content">
                      <div className="interaction-stat-label">Audio Plays</div>
                      <div className="interaction-stat-value">{statistics.interactionStats?.audio_play?.total || 0}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Clicked Links */}
              {Object.keys(statistics.interactionStats?.link_click?.byButton || {}).length > 0 && (
                <div className="stats-section">
                  <h2>🔗 Top Clicked Links</h2>
                  <div className="top-interactions-list">
                    {Object.entries(statistics.interactionStats.link_click.byButton)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 15)
                      .map(([button, count], index) => (
                        <div key={index} className="interaction-item">
                          <div className="interaction-rank">#{index + 1}</div>
                          <div className="interaction-info">
                            <div className="interaction-name">{button}</div>
                            <div className="interaction-count">{count} clicks</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Top Popups Opened */}
              {Object.keys(statistics.interactionStats?.popup_open?.byPopup || {}).length > 0 && (
                <div className="stats-section">
                  <h2>📦 Top Popups Opened</h2>
                  <div className="top-interactions-list">
                    {Object.entries(statistics.interactionStats.popup_open.byPopup)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 15)
                      .map(([popup, count], index) => (
                        <div key={index} className="interaction-item">
                          <div className="interaction-rank">#{index + 1}</div>
                          <div className="interaction-info">
                            <div className="interaction-name">{popup}</div>
                            <div className="interaction-count">{count} opens</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Top Audio Plays */}
              {Object.keys(statistics.interactionStats?.audio_play?.byTrack || {}).length > 0 && (
                <div className="stats-section">
                  <h2>🎵 Top Audio Plays</h2>
                  <div className="top-interactions-list">
                    {Object.entries(statistics.interactionStats.audio_play.byTrack)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 15)
                      .map(([track, count], index) => (
                        <div key={index} className="interaction-item">
                          <div className="interaction-rank">#{index + 1}</div>
                          <div className="interaction-info">
                            <div className="interaction-name">{track}</div>
                            <div className="interaction-count">{count} plays</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Recent Interactions */}
              <div className="stats-section">
                <h2>🕐 Recent Interactions</h2>
                <div className="recent-interactions-list">
                  {statistics.interactions?.slice(0, 50).map((interaction, index) => (
                    <div key={index} className="recent-interaction-item">
                      <div className="interaction-icon">{getInteractionIcon(interaction.interactionType)}</div>
                      <div className="interaction-details">
                        <div className="interaction-type">{getInteractionLabel(interaction.interactionType)}</div>
                        <div className="interaction-description">{formatInteractionDetails(interaction)}</div>
                        <div className="interaction-meta">
                          <span className="interaction-time">{formatTimestamp(interaction.timestamp)}</span>
                          {interaction.userEmail && (
                            <span className="interaction-user">👤 {interaction.userEmail}</span>
                          )}
                          {!interaction.userEmail && (
                            <span className="interaction-user anonymous">Anonymous</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!statistics.interactions || statistics.interactions.length === 0) && (
                    <div className="no-data">No interactions recorded yet. Interactions will appear here as users click buttons and open popups.</div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* USERS TAB */}
          {activeStatsTab === 'users' && <VisitorUsersTab users={statistics.loggedInUsers || []} />}

          {/* SHARE CARDS TAB */}
          {activeStatsTab === 'share' && (
            <VisitorShareTab events={shareEvents} loading={shareLoading} error={shareError} onRetry={loadShareEvents} />
          )}

          {/* COMPARISONS TAB */}
          {activeStatsTab === 'comparisons' && (
            <VisitorComparisonsTab events={comparisonEvents} loading={comparisonLoading} error={comparisonError} onRetry={loadComparisonEvents} />
          )}

          {/* OVERVIEW TAB */}
          {activeStatsTab === 'overview' && (
            <>
              {/* Daily Visits Chart */}
              <div className="stats-section">
                <h2>📈 Daily Visits Trend</h2>
                <div className="chart-container">
                  <div className="bar-chart">
                    {statistics.dailyStats.map((day, index) => {
                      const maxVisits = Math.max(...statistics.dailyStats.map(d => d.visits));
                      const heightPercent = (day.visits / maxVisits) * 100;
                      return (
                        <div key={index} className="bar-wrapper">
                          <div 
                            className="bar" 
                            style={{ '--bar-h': heightPercent || 0 }}
                            title={`${day.date}: ${day.visits} visits`}
                          >
                            <span className="bar-label">{day.visits}</span>
                          </div>
                          <div className="bar-date">{formatDate(day.date)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="stats-grid">
                {/* Top Pages */}
                <div className="stats-section">
                  <h2>🏆 Top Pages</h2>
                  <div className="top-pages-list">
                    {statistics.topPages.slice(0, 15).map((page, index) => (
                  <div key={index} className="top-page-item">
                    <div className="page-rank">#{index + 1}</div>
                    <div className="page-info">
                      <div className="page-path">{page.page}</div>
                      <div className="page-stats">
                        <span className="page-views">{page.views} views</span>
                        <span className="page-percentage">
                          ({((page.views / statistics.totalVisits) * 100).toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                    <div className="page-bar-wrapper">
                      <div 
                        className="page-bar" 
                        style={{ 
                          width: `${(page.views / statistics.topPages[0].views) * 100}%` 
                        }}
                      ></div>
                    </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Device Stats */}
              <div className="stats-section">
                <h2>📱 Device Breakdown</h2>
                <div className="device-stats">
                  {Object.entries(statistics.deviceStats)
                    .sort((a, b) => b[1] - a[1])
                    .map(([device, count]) => {
                      const percentage = (count / statistics.totalVisits) * 100;
                      return (
                        <div key={device} className="device-item">
                          <div className="device-header">
                            <span className="device-icon">
                              {device === 'mobile' ? '📱' : device === 'tablet' ? '📲' : '💻'}
                            </span>
                            <span className="device-name">{device.charAt(0).toUpperCase() + device.slice(1)}</span>
                            <span className="device-count">{count.toLocaleString()}</span>
                          </div>
                          <div className="device-bar-wrapper">
                            <div 
                              className="device-bar" 
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <div className="device-percentage">{percentage.toFixed(1)}%</div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Browser Stats */}
              <div className="stats-section">
                <h2>🌐 Browser Distribution</h2>
                <div className="browser-stats">
                  {Object.entries(statistics.browserStats)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 10)
                    .map(([browser, count]) => {
                      const percentage = (count / statistics.totalVisits) * 100;
                      return (
                        <div key={browser} className="browser-item">
                          <div className="browser-header">
                            <span className="browser-name">{browser}</span>
                            <span className="browser-count">{count.toLocaleString()}</span>
                          </div>
                          <div className="browser-bar-wrapper">
                            <div 
                              className="browser-bar" 
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <div className="browser-percentage">{percentage.toFixed(1)}%</div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>

              {/* Recent Visits Table */}
              <div className="stats-section">
                <h2>🕐 Recent Visits</h2>
                <div className="recent-visits-table-wrapper">
                  <table className="recent-visits-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Page</th>
                        <th>Device</th>
                        <th>Browser</th>
                        <th>Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statistics.recentVisits.map((visit, index) => (
                        <tr key={index}>
                          <td className="visit-time" data-label="Time">{formatTimestamp(visit.timestamp)}</td>
                          <td className="visit-page" data-label="Page">{visit.page || visit.path || 'Unknown'}</td>
                          <td className="visit-device" data-label="Device">
                            {detectDevice(visit.userAgent)}
                          </td>
                          <td className="visit-browser" data-label="Browser">
                            {detectBrowser(visit.userAgent)}
                          </td>
                          <td className="visit-location" data-label="Location">
                            {visit.country || visit.city || visit.ip || 'Unknown'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
